import { useCompany } from "@/api/services/admin/company";
import { createCompanyUserApi } from "@/api/services/admin/company/user";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Select } from "@/components/elements/Select";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { createCompanyUserSchema } from "@/features/company/schema/companyUserSchema";
import { TCreateCompanyUserForm } from "@/features/company/types/createCompanyUserForm";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { showError } from "@/utils/error";
import { getDynamicRoute } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { IconWarning } from "@/assets/icons/IconWarning";
import { orange, text, background } from "@/theme/colors";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { InnerBox } from "@/components/elements/InnerBox";
import { Button } from "@/components/elements/Button";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { IconSend } from "@/assets/icons";

const initCreateForm: Partial<TCreateCompanyUserForm> = {
  fullName: "",
  email: "",
};

export const CreateCompanyUserForm = () => {
  const toast = useToast();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: companyResult } = useCompany();
  const companyName = companyResult?.data?.companyName;
  const [loading, setLoading] = useState(false);
  const methods = useForm<TCreateCompanyUserForm>({
    defaultValues: initCreateForm,
    resolver: yupResolver(createCompanyUserSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });
  const { control, watch, trigger } = methods;
  const isUser = watch("isUser");

  // Trigger email validation when isUser changes
  useEffect(() => {
    if (isUser !== undefined && isUser !== null) {
      trigger("email");
    }
  }, [isUser, trigger]);

  const onSubmit = async (data: TCreateCompanyUserForm) => {
    if (!id) return;
    setLoading(true);
    try {
      const payload = { ...data, isUser: Boolean(data.isUser) };
      const rs = await createCompanyUserApi(id, payload);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      navigate(getDynamicRoute(AppRoutes.companyDetail, { id }));
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY.FETCH_USER_LIST] });
    } catch (error) {
      showError(error);
      throw new Error();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form methods={methods} onSubmit={onSubmit} schema={createCompanyUserSchema} initialValues={initCreateForm}>
      <MenuNavigate
        contentWidth="900px"
        contents={[
          {
            title: "ユーザーの状態",
            content: (
              <InnerBox title="ユーザーの状態" padding={3}>
                <Grid container columnSpacing={3} rowSpacing={2} alignItems="flex-end">
                  <Grid item xs={5}>
                    <Stack gap={0.5}>
                      <Typography variant="cap12" color={text.secondary}>
                        ステータス *
                      </Typography>
                      <Typography variant="body14" color={text.primary}>
                        使用中
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={4}>
                    <Stack gap={0.5}>
                      <Typography variant="cap12" color={text.secondary}>
                        認証ステータス
                      </Typography>
                      <Typography variant="body14" color={text.primary}>
                        登録メール送信前
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={3} textAlign="right">
                    <Button
                      size="sm"
                      disabled
                      sx={{
                        "&.Mui-disabled": {
                          border: "none !important",
                          backgroundColor: `${background.disable} !important`,
                          color: `${text.tertiary} !important`,
                        },
                      }}
                    >
                      パスワードリセット
                    </Button>
                  </Grid>
                </Grid>
              </InnerBox>
            ),
          },
          {
            title: "基本情報",
            content: (
              <InnerBox title="契約情報" padding={3}>
                <Grid container columnSpacing={3} rowSpacing={2}>
                  <Grid item xs={6}>
                    <FormField label="顧客名">
                      <Input value={companyName || ""} disabled />
                    </FormField>
                  </Grid>
                  <Grid item xs={6} />
                  <Grid item xs={6}>
                    <FormField control={control} name="fullName">
                      <Input placeholder="氏名" />
                    </FormField>
                  </Grid>
                  <Grid item xs={6}>
                    <FormField control={control} name="email">
                      <Input placeholder="メールアドレス" />
                    </FormField>
                  </Grid>
                  <Grid item xs={6}>
                    <FormField control={control} name="isUser">
                      <Select
                        placeholder="顧客ユーザー / irohana担当者"
                        options={[
                          { label: "irohana担当者", value: 0 },
                          { label: "顧客ユーザー", value: 1 },
                        ]}
                      />
                      <Stack direction="row" alignContent="center" mt={1}>
                        <IconWarning />
                        <Typography variant="cap12" color={orange[80]}>
                          当社がログインするためのユーザーは「irohana担当者」に変更すること
                        </Typography>
                      </Stack>
                    </FormField>
                  </Grid>
                </Grid>
              </InnerBox>
            ),
          },
        ]}
        contentAffix={
          <FooterContainer>
            <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
              <Button size="sm-round" variant="secondary" onClick={() => navigate(getDynamicRoute(AppRoutes.companyDetail, { id }))}>
                戻る
              </Button>
              <Stack direction="row" gap={3}>
                <Button
                  disabled={loading}
                  type="submit"
                  size="sm"
                  startIcon={<IconSend />}
                  sx={{
                    "& .MuiButton-startIcon svg path": {
                      fill: "currentColor",
                    },
                  }}
                >
                  メールを送信
                </Button>
              </Stack>
            </Stack>
          </FooterContainer>
        }
      />
    </Form>
  );
};
